import { computed, inject, isDevMode } from '@angular/core';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';
import { mapResponse } from '@ngrx/operators';
import { concat, debounceTime, exhaustMap, groupBy, mergeMap, of, switchMap } from 'rxjs';
import {
  findInitialFixtureIndex,
  Fixture,
  FixtureMatchStats,
  fixturesEvents,
  GameScoreValues,
  MATCH_CONTEXT,
  Player,
  PREDICTION_GAME_DISPLAY_CONFIG,
  PREDICTION_GAME_SERVICE,
  PREDICTION_GAME_TENANT_CONFIG,
} from '@kikkz/shared/prediction-game/util';
import { SNACKBAR_SERVICE } from '@kikkz/shared/snackbar/util';
import { TRANSLATION_SERVICE } from '@kikkz/shared/i18n/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { SAVE_DEBOUNCE_MS, serverErrorMessage } from './server-error-message';
import { asyncReducers } from '@kikkz/shared/state/util';

export interface FixturesState {
  fixtures: Fixture[];
  players: Player[];
  gameScoreValues: GameScoreValues | null;
  selectedFixtureIndex: number;
  isLoadingFixtures: boolean;
  /** The fixture list arrived at least once — tells "not requested yet" from "really empty". */
  hasLoadedFixtures: boolean;
  isLoadingPlayers: boolean;
  isLoadingGameScoreValues: boolean;
  /**
   * Fixtures with an edit pending — set from the first edit, so it includes the debounce
   * window. Tracked per fixture (not as one global flag) because saves are debounced per
   * fixture: an edit on match A followed within the debounce window by an edit on match B
   * must persist BOTH.
   */
  savingFixtureIds: string[];
  /** Fixtures whose save request is actually in flight (UI must lock). */
  persistingFixtureIds: string[];
  /**
   * The prediction each fixture had before the first optimistic edit of the current
   * burst — restored when the save ultimately fails, so a rejected save never leaves the
   * optimistic value on screen.
   */
  predictionSnapshots: Record<string, FixtureMatchStats | null>;
  // Per-section error state — each data region fails (and recovers) independently
  // so a failure in one area never blanks out or pollutes another. The squad and the
  // scoring config are accessories to the fixture list: nothing renders an error card for
  // them, their failure is a snackbar (unless the host turns it off) and feeds the
  // facade's `matchDataLoadFailed`.
  fixturesError: string | null;
  playersError: string | null;
  gameScoreValuesError: string | null;
  savePredictionError: string | null;
}

const initialState: FixturesState = {
  fixtures: [],
  players: [],
  gameScoreValues: null,
  selectedFixtureIndex: 0,
  isLoadingFixtures: false,
  hasLoadedFixtures: false,
  isLoadingPlayers: false,
  isLoadingGameScoreValues: false,
  savingFixtureIds: [],
  persistingFixtureIds: [],
  predictionSnapshots: {},
  fixturesError: null,
  playersError: null,
  gameScoreValuesError: null,
  savePredictionError: null,
};

/** Adds `id` to a tracking list without duplicating it (immer draft, mutated in place). */
function track(ids: string[], id: string): void {
  if (!ids.includes(id)) ids.push(id);
}

/**
 * Fixtures region of the prediction game: the match list, the squad, the scoring config,
 * which match is selected and the user's per-match prediction (optimistically applied,
 * debounced per fixture, rolled back on failure).
 */
export const FixturesStore = signalStore(
  withState(initialState),
  withDevtools('prediction-game-fixtures'),
  withComputed((store) => ({
    selectedFixture: computed(() => store.fixtures()[store.selectedFixtureIndex()] ?? null),
    /** An edit is pending somewhere (includes the debounce window). */
    isSavingPrediction: computed(() => store.savingFixtureIds().length > 0),
    /** A save request is in flight somewhere — the prediction UI locks on this. */
    isPersistingPrediction: computed(() => store.persistingFixtureIds().length > 0),
  })),
  withReducer(
    ...asyncReducers<FixturesState>()({
      load: fixturesEvents.loadFixtures,
      success: fixturesEvents.loadFixturesSuccess,
      failed: fixturesEvents.loadFixturesFailed,
      loading: 'isLoadingFixtures',
      error: 'fixturesError',
      onSuccess: (state, payload) => {
        state.fixtures = payload.fixtures;
        state.hasLoadedFixtures = true;
        state.selectedFixtureIndex = payload.initialIndex;
      },
    }),
    ...asyncReducers<FixturesState>()({
      load: fixturesEvents.loadPlayers,
      success: fixturesEvents.loadPlayersSuccess,
      failed: fixturesEvents.loadPlayersFailed,
      loading: 'isLoadingPlayers',
      error: 'playersError',
      onSuccess: (state, payload) => {
        state.players = payload.players;
      },
    }),
    ...asyncReducers<FixturesState>()({
      load: fixturesEvents.loadGameScoreValues,
      success: fixturesEvents.loadGameScoreValuesSuccess,
      failed: fixturesEvents.loadGameScoreValuesFailed,
      loading: 'isLoadingGameScoreValues',
      error: 'gameScoreValuesError',
      onSuccess: (state, payload) => {
        state.gameScoreValues = payload.gameScoreValues;
      },
    }),
    immerOn(fixturesEvents.savePrediction, (state, { payload }) => {
      track(state.savingFixtureIds, payload.fixtureId);
      state.savePredictionError = null;
      const index = state.fixtures.findIndex((f) => f.id === payload.fixtureId);
      if (index === -1) return;
      // Remember the last persisted prediction once per burst, so a failed save can put
      // it back (the later edits of the same burst must not overwrite the snapshot).
      if (!(payload.fixtureId in state.predictionSnapshots)) {
        state.predictionSnapshots[payload.fixtureId] = state.fixtures[index].userPrediction;
      }
      // Optimistically apply the prediction so subsequent edits merge onto the latest
      // state — required because the debounced save only persists the final event of a
      // burst, and the merge in predict-tab reads back the fixture's userPrediction.
      state.fixtures[index] = { ...state.fixtures[index], userPrediction: payload.prediction };
    }),
    immerOn(fixturesEvents.savePredictionRequestStarted, (state, { payload }) => {
      track(state.persistingFixtureIds, payload.fixtureId);
    }),
    immerOn(fixturesEvents.savePredictionSuccess, (state, { payload }) => {
      const fixtureId = payload.fixture.id;
      state.savingFixtureIds = state.savingFixtureIds.filter((id) => id !== fixtureId);
      state.persistingFixtureIds = state.persistingFixtureIds.filter((id) => id !== fixtureId);
      delete state.predictionSnapshots[fixtureId];
      const index = state.fixtures.findIndex((f) => f.id === fixtureId);
      if (index !== -1) {
        const previous = state.fixtures[index];
        // The PUT /fixture response may omit the editability flags, which the mapper
        // then defaults to true. Deadlines only ever move towards locked, so a save
        // must never UNLOCK a fixture the fixtures list already reported as locked.
        state.fixtures[index] = {
          ...payload.fixture,
          userPredictionEditable: previous.userPredictionEditable && payload.fixture.userPredictionEditable,
          userPredictionLineupEditable:
            previous.userPredictionLineupEditable && payload.fixture.userPredictionLineupEditable,
        };
      }
    }),
    immerOn(fixturesEvents.savePredictionFailed, (state, { payload }) => {
      const { fixtureId } = payload;
      state.savingFixtureIds = state.savingFixtureIds.filter((id) => id !== fixtureId);
      state.persistingFixtureIds = state.persistingFixtureIds.filter((id) => id !== fixtureId);
      state.savePredictionError = payload.error;
      // Roll the optimistic prediction back: the server rejected it, so keeping it on
      // screen would tell the user their tip is saved when it is not.
      if (fixtureId in state.predictionSnapshots) {
        const snapshot = state.predictionSnapshots[fixtureId];
        delete state.predictionSnapshots[fixtureId];
        const index = state.fixtures.findIndex((f) => f.id === fixtureId);
        if (index !== -1) {
          state.fixtures[index] = { ...state.fixtures[index], userPrediction: snapshot };
        }
      }
    }),
    immerOn(fixturesEvents.selectFixture, (state, { payload }) => {
      state.selectedFixtureIndex = payload.index;
    }),
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      service = inject(PREDICTION_GAME_SERVICE),
      snackbar = inject(SNACKBAR_SERVICE),
      translation = inject(TRANSLATION_SERVICE),
      logger = inject(LOGGING_SERVICE),
      matchContext = inject(MATCH_CONTEXT),
      tenant = inject(PREDICTION_GAME_TENANT_CONFIG),
      displayConfig = inject(PREDICTION_GAME_DISPLAY_CONFIG),
    ) => ({
      loadFixtures$: events.on(fixturesEvents.loadFixtures).pipe(
        exhaustMap(() =>
          service.loadFixtures().pipe(
            mapResponse({
              // Initial selection: the host app's heimspielID always wins; without one
              // (or when it matches nothing) the tenant's configured rule decides.
              next: (fixtures) =>
                fixturesEvents.loadFixturesSuccess({
                  fixtures,
                  initialIndex: findInitialFixtureIndex(fixtures, {
                    heimspielId: matchContext.heimspielId(),
                    rule: tenant.initialFixtureRule,
                    // The shortened dev-testing window never applies to a production build,
                    // regardless of what the tenant config sets.
                    graceMinutes: isDevMode() ? (tenant.devSelectionGraceMinutes ?? null) : null,
                  }),
                }),
              error: (error: unknown) =>
                fixturesEvents.loadFixturesFailed({
                  error: serverErrorMessage(error, 'pg.errors.fixturesLoad', translation, logger),
                }),
            }),
          ),
        ),
      ),
      loadPlayers$: events.on(fixturesEvents.loadPlayers).pipe(
        exhaustMap(() =>
          service.loadPlayers().pipe(
            mapResponse({
              next: (players) => fixturesEvents.loadPlayersSuccess({ players }),
              // Nothing renders an error card for the squad — without the snackbar a failed
              // load would only show up as permanently empty player pickers. A host with a
              // full-view error state opts out via `loadErrorSnackbar`.
              error: (error: unknown) => {
                const message = serverErrorMessage(error, 'pg.errors.matchDataLoad', translation, logger);
                if (displayConfig.loadErrorSnackbar) snackbar.error(message);
                return fixturesEvents.loadPlayersFailed({ error: message });
              },
            }),
          ),
        ),
      ),
      loadGameScoreValues$: events.on(fixturesEvents.loadGameScoreValues).pipe(
        exhaustMap(() =>
          service.loadGameScoreValues().pipe(
            mapResponse({
              next: (gameScoreValues) => fixturesEvents.loadGameScoreValuesSuccess({ gameScoreValues }),
              // Same as the squad: no error card exists for the scoring config, so the
              // snackbar is the only place this failure can become visible.
              error: (error: unknown) => {
                const message = serverErrorMessage(error, 'pg.errors.matchDataLoad', translation, logger);
                if (displayConfig.loadErrorSnackbar) snackbar.error(message);
                return fixturesEvents.loadGameScoreValuesFailed({ error: message });
              },
            }),
          ),
        ),
      ),
      // Debounced PER FIXTURE: `groupBy` gives every match its own debounce window and
      // its own `switchMap`, so paging to the next match right after an edit can never
      // discard the first match's pending save — while rapid edits WITHIN one match
      // still collapse into a single request.
      //
      // `switchMap` (not `concatMap`) is deliberate: within a group, a newer edit of the
      // SAME match makes the request in flight obsolete, so cancelling it is correct — the
      // PUT always carries the complete prediction, so the last request wins regardless of
      // whether the aborted one still reached the server. Because the groups are keyed by
      // fixture, this only ever cancels a save of the very match being edited; another
      // match's save runs in its own group (`mergeMap`) and is never touched.
      //
      // `groupBy` intentionally has no duration selector: the groups live as long as the
      // store. A season has a bounded number of fixtures (and only those the user actually
      // edits ever open a group), so the retained subjects are a handful at most — whereas
      // closing a group early would risk dropping an edit that is still inside its debounce
      // window.
      savePrediction$: events.on(fixturesEvents.savePrediction).pipe(
        groupBy(({ payload }) => payload.fixtureId),
        mergeMap((perFixture) =>
          perFixture.pipe(
            debounceTime(SAVE_DEBOUNCE_MS),
            switchMap(({ payload }) =>
              concat(
                of(fixturesEvents.savePredictionRequestStarted({ fixtureId: payload.fixtureId })),
                service.savePrediction(payload.fixtureId, payload.prediction).pipe(
                  mapResponse({
                    next: (fixture) => fixturesEvents.savePredictionSuccess({ fixture }),
                    error: (error: unknown) => {
                      const message = serverErrorMessage(error, 'pg.errors.predictionSave', translation, logger);
                      snackbar.error(message);
                      return fixturesEvents.savePredictionFailed({ fixtureId: payload.fixtureId, error: message });
                    },
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    }),
  ),
);
